import type { LoveReason, SectionRow } from '@/types'

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { heartShower } from '@/lib/celebrate'

import {
    Heart,
    Quote,
    RotateCcw,
    Sparkles
} from 'lucide-react'

import Reveal from '@/components/landing/reveal'
import SectionHeading from '@/components/landing/section-heading'

interface ReasonsSectionProps {
    section?: SectionRow
    reasons: Array<LoveReason>
}

const pad = (value: number): string => String(value).padStart(2, '0')

const FRONTS = [
    'from-petal via-petal-deep to-wine',
    'from-[#f07a98] via-petal to-petal-deep',
    'from-petal-deep via-wine to-plum',
    'from-[#ff8fae] via-[#e8547c] to-[#a3204a]'
]

export default function ReasonsSection({ section, reasons }: ReasonsSectionProps) {
    const [flipped, setFlipped] = useState<Array<string>>([])
    const [celebrated, setCelebrated] = useState(false)
    const total = reasons.length
    const readCount = reasons.filter(reason => flipped.includes(reason.id)).length
    const allRead = total > 0 && readCount === total

    useEffect(() => {
        if (!allRead || celebrated) return

        setCelebrated(true)
        void heartShower()
    }, [allRead, celebrated])

    const toggle = (id: string) => {
        setFlipped(current => current.includes(id) ? current.filter(item => item !== id) : [...current, id])
    }

    const toggleAll = () => {
        setFlipped(allRead ? [] : reasons.map(reason => reason.id))
    }

    return (
        <section id='reasons' className='relative overflow-hidden bg-cream py-24 sm:py-32'>
            <div aria-hidden className='absolute top-1/3 -right-32 size-96 rounded-full bg-blush blur-3xl' />

            <div className='relative mx-auto max-w-6xl px-6 lg:px-10'>
                <SectionHeading section={section} />

                <Reveal className='mt-10 flex flex-wrap items-center justify-center gap-3' delay={150}>
                    <p className='rounded-full bg-white px-4 py-2 text-sm font-semibold text-wine shadow-sm ring-1 ring-blush' aria-live='polite'>
                        <Heart aria-hidden className='mr-1.5 inline size-4 -translate-y-px fill-petal text-petal' />
                        {readCount} / {total} padh liye
                    </p>

                    <button
                        type='button'
                        onClick={toggleAll}
                        className='inline-flex items-center gap-2 rounded-full bg-wine px-4 py-2 text-sm font-semibold text-cream shadow-sm transition outline-none hover:bg-petal-deep focus-visible:ring-4 focus-visible:ring-petal/40'
                    >
                        {allRead ? <RotateCcw aria-hidden className='size-4' /> : <Sparkles aria-hidden className='size-4' />}
                        {allRead ? 'Saare wapas palto' : 'Saare ek saath palto'}
                    </button>
                </Reveal>

                <ul className='mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-6 lg:grid-cols-4'>
                    {reasons.map((reason, index) => {
                        const isFlipped = flipped.includes(reason.id)

                        return (
                            <li key={reason.id}>
                                <Reveal delay={(index % 4) * 80}>
                                    <button
                                        type='button'
                                        onClick={() => toggle(reason.id)}
                                        aria-pressed={isFlipped}
                                        className='group block aspect-[3/4] w-full rounded-3xl text-left outline-none [perspective:1100px] focus-visible:ring-4 focus-visible:ring-petal/40'
                                    >
                                        <span
                                            className={cn(
                                                'relative block size-full transition-transform duration-700 ease-[cubic-bezier(0.3,0.7,0.2,1)] [transform-style:preserve-3d] motion-reduce:duration-0',
                                                isFlipped ? '[transform:rotateY(180deg)]' : 'group-hover:[transform:rotateY(8deg)]'
                                            )}
                                        >
                                            <span
                                                aria-hidden={isFlipped}
                                                className={cn(
                                                    'absolute inset-0 flex flex-col items-center justify-center rounded-3xl bg-gradient-to-br p-4 text-cream shadow-[0_20px_40px_-22px_rgba(91,21,48,0.8)] [backface-visibility:hidden]',
                                                    FRONTS[index % FRONTS.length]
                                                )}
                                            >
                                                <span aria-hidden className='absolute inset-2.5 rounded-[1.1rem] border border-white/25' />

                                                <span className='text-[0.65rem] font-bold tracking-[0.32em] text-cream/75 uppercase'>
                                                    Reason
                                                </span>

                                                <span className='mt-1 font-heading text-5xl font-semibold sm:text-6xl'>
                                                    {pad(index + 1)}
                                                </span>

                                                <Heart aria-hidden className='mt-4 size-6 fill-cream/90 text-cream/90 group-hover:animate-heartbeat motion-reduce:animate-none' />

                                                <span className='absolute bottom-5 text-xs font-semibold text-cream/75'>
                                                    Tap karo
                                                </span>
                                            </span>

                                            <span
                                                aria-hidden={!isFlipped}
                                                className='absolute inset-0 flex flex-col items-center justify-center overflow-hidden rounded-3xl bg-white p-4 text-center shadow-[0_20px_40px_-24px_rgba(91,21,48,0.6)] ring-1 ring-blush [backface-visibility:hidden] [transform:rotateY(180deg)] sm:p-5'
                                            >
                                                <Quote aria-hidden className='size-5 shrink-0 text-petal/60' />

                                                {reason.reason && (
                                                    <span
                                                        data-mdlfy-cms={`love_reasons:${reason.id}:reason`}
                                                        className='mt-3 block font-hand text-lg leading-snug text-ink sm:text-xl lg:text-[1.35rem]'
                                                    >
                                                        {reason.reason}
                                                    </span>
                                                )}

                                                <Heart aria-hidden className='mt-3 size-4 shrink-0 fill-petal text-petal' />
                                            </span>
                                        </span>
                                    </button>
                                </Reveal>
                            </li>
                        )
                    })}
                </ul>

                {allRead && (
                    <p className='mx-auto mt-12 max-w-lg animate-in text-center font-heading text-xl text-balance text-wine italic duration-700 fade-in slide-in-from-bottom-3'>
                        Saare reasons padh liye? Baaki ke sunne ke liye mujhse milna padega.
                    </p>
                )}
            </div>
        </section>
    )
}
